import { useState } from 'react';
import { Loader2, Package } from 'lucide-react';
import { toast } from 'sonner';
import { Button, Input, Label } from '@staff-app/ui';
import { errorText, validateApkUrl } from '../lib/device-commands';
import {
  TestingConfirm,
  TestingDialogFrame,
  type TestingCommandDialogProps,
} from './TestingDialogFrame';

/**
 * More › Install APK — port of scio3 `InstallApkDialog`: addCommandExecution MDM_INSTALL_APK with the
 * typed URL as the value (scio1 URL check first). Confirmed before sending.
 */
export function TestingInstallApkDialog({
  open,
  deviceName,
  commands,
  onClose,
  onDone,
}: TestingCommandDialogProps) {
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);

  const review = () => {
    const problem = validateApkUrl(url);
    setError(problem);
    if (!problem) setConfirming(true);
  };

  const install = async () => {
    setConfirming(false);
    setSending(true);
    try {
      await commands.addCommandExecution('MDM_INSTALL_APK', url.trim());
      toast.success('Install APK sent');
      onDone();
      onClose();
    } catch (err) {
      toast.error(errorText(err, 'Failed to install APK'));
    } finally {
      setSending(false);
    }
  };

  return (
    <TestingDialogFrame
      open={open}
      onClose={onClose}
      title={'Install APK'}
      icon={Package}
      testId="testing-install-apk-dialog"
      footer={
        <Button
          variant="destructive"
          onClick={review}
          disabled={!url.trim() || sending}
          disabledTooltip={!url.trim() && !sending ? 'Enter the APK URL' : undefined}
          data-testid="testing-install-apk-submit"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : null}
          {'Install'}
        </Button>
      }
    >
      <div className="space-y-2">
        <Label htmlFor="testing-install-apk-url">{'APK URL'}</Label>
        <Input
          id="testing-install-apk-url"
          placeholder={'https://…/app.apk'}
          value={url}
          onChange={(e) => {
            setUrl(e.target.value);
            setError('');
          }}
          aria-invalid={!!error}
        />
        {error ? (
          <p className="text-xs text-destructive" data-testid="testing-install-apk-error">
            {error}
          </p>
        ) : null}
      </div>

      <TestingConfirm
        open={confirming}
        title={'Install APK'}
        description={`Install ${url.trim()} on ${deviceName}?`}
        confirmLabel={'Install'}
        onConfirm={() => void install()}
        onCancel={() => setConfirming(false)}
      />
    </TestingDialogFrame>
  );
}
